import { Layout } from "react-grid-layout";

import {
  SendMessageDto,
  ToteEventDto,
  SignalRPickEventDto,
  PTLEventDto,
  AutostoreEvent,
  AndonEvent,
  HeartbeatEvent,
  BinFlagEvent,
  WorkstationSummaryDto
} from "~/types/api";

export type AndonViewOption = "board fullscreen" | "default";

export type AndonStatus = "good" | "bad";

export type PongData = {
  // --- Universal (all modern browsers) ---
  userAgent: string; // Legacy UA string (e.g., "Mozilla/5.0 (Macintosh; Intel Mac OS X...)")
  userAgentParsed: {
    browser: string; // e.g., "Chrome", "Firefox", "Safari"
    version: string; // e.g., "134.0.0.0"
    os: string; // e.g., "macOS 10.15.7", "Windows 11"
    platform: string; // e.g., "desktop", "mobile", "tablet"
    isMobile: boolean; // true if the device is likely a mobile or tablet
  };
  cpu: {
    cores: number; // Number of logical CPU cores (e.g., 2, 4, 8)
  };
  fps: number; // Estimated frames per second (e.g., 60, 45, 20)

  // --- Chromium-only: Low Entropy UA Client Hints ---
  userAgentLowEntropy: {
    brands: Array<{ brand: string; version: string }>; // e.g., [{ brand: "Chromium", version: "134" }]
    mobile: boolean; // True if the device is mobile
    platform: string; // e.g., "Windows", "macOS", "Android"
  };

  // --- Chromium-only: High Entropy UA Client Hints ---
  userAgentHighEntropy: {
    architecture: string; // e.g., "x86", "x86_64", "arm"
    bitness: string; // e.g., "64", "32"
    model: string; // e.g., "Pixel 7 Pro", "iPhone14,3"
    platform: string; // e.g., "macOS", "Windows"
    platformVersion: string; // e.g., "10.15.7", "14.4.1"
    uaFullVersion: string; // e.g., "134.0.7564.89"
    fullVersionList: Array<{ brand: string; version: string }>; // e.g., [{ brand: "Google Chrome", version: "134.0.7564.89" }]
  };

  // --- Chromium-only: Network Information API ---
  network: {
    effectiveType: string; // e.g., "4g", "3g", "slow-2g"
    rtt: number; // Estimated round-trip latency in ms (e.g., 50, 150)
    downlink: number; // Estimated download speed in Mbps (e.g., 1.5, 10, 100)
  };

  // --- Chromium-only: performance.memory ---
  memory: {
    usedJSHeapSize: number; // Current JS heap usage in bytes (e.g., 1_200_000)
    totalJSHeapSize: number; // Total allocated heap in bytes (e.g., 3_000_000)
    jsHeapSizeLimit: number; // Max JS heap limit in bytes (e.g., 4_294_967_296)
  };

  screen: {
    width: number; // Screen width in pixels (e.g., 1920)
    height: number; // Screen height in pixels (e.g., 1080)
    availWidth: number; // Available screen width in pixels (e.g., 1900)
    availHeight: number; // Available screen height in pixels (e.g., 1050)
    pixelRatio: number; // Device pixel ratio (e.g., 1.0, 2.0)
    innerWidth: number; // Inner width of the browser window in pixels (e.g., 1800)
    innerHeight: number; // Inner height of the browser window in pixels (e.g., 900)
  };

  timeZone: string; // e.g., "America/New_York"
  timeZoneOffsetMinutes: number; // e.g., -240 (for EDT)
  workstationDeviceId: string; // e.g., "Workstation 1"
  clientVersion: string; // Frontend version (e.g., "v2.1.0" or commit hash)
  serverVersion: {
    tag: string; // e.g., "v2.1.0"
    hash: string; // e.g., "abc123def"
  } | null; // null if server version not yet fetched

  // --- Navigation Performance Metrics ---
  // Helps diagnose cache issues by showing whether the page was served from cache
  navigation: {
    type: string; // 'navigate', 'reload', 'back_forward', 'prerender'
    transferSize: number; // 0 = from cache, >0 = from network (bytes)
    duration: number; // Total navigation time in ms
  };
};

export type WorkstationPongData = {
  pongData: PongData;
  pongLoopTime: number;
  batchTimestamp: string | null;
  timePingSent: string;
};

export type MixedEventData =
  | SendMessageDto
  | ToteEventDto
  | SignalRPickEventDto
  | PTLEventDto
  | AutostoreEvent
  | AndonEvent
  | BinFlagEvent
  | HeartbeatEvent;

export type AltTextKey =
  | "hand raised"
  | "port fault"
  | "grid error"
  | "active"
  | "inactive";

export type AndonColorState = {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  icon: React.ElementType | null;
  altTextKey?: AltTextKey;
};

export type AndonEventWithMeta = {
  event: MixedEventData;
  hub: "grid" | "pick" | "ptl" | "tote" | "andon" | "bin-flag";
  timestamp: Date;
  portId?: string;
  gridId?: string;
  workstationId?: string;
  binId?: string;
  status?: AndonStatus;
};

export type AutostoreEventOfCase<K extends AutostoreEvent["case"]> = Extract<
  AutostoreEvent,
  { case: K }
>;

export type StructuredEvent =
  | ToteEventDto
  | SignalRPickEventDto
  | PTLEventDto
  | AndonEvent
  | BinFlagEvent
  | AutostoreEvent;

export type AndonBoardWorkstationLayoutItem = Layout & {
  /** Optional for legacy saved layouts; missing kind means workstation. */
  kind?: "workstation";
  fulfillmentCenterId?: string;
};

export type AndonBoardTextLayoutItem = Layout & {
  kind: "text";
  text: string;
  backgroundColor?: string;
  textColor?: string;
  fontSize?: number;
};

export type AndonBoardLayoutItem =
  | AndonBoardWorkstationLayoutItem
  | AndonBoardTextLayoutItem;

export type AndonBoardWorkstationSelection = {
  workstationId: string;
  fulfillmentCenterId: string;
};

export type AndonDisplayLine =
  | "icon"
  | "status"
  | "deviceId"
  | "userName"
  | "fcAbbreviation"
  | "roles";

export const DEFAULT_ANDON_DISPLAY_LINES: AndonDisplayLine[] = [
  "icon",
  "status",
  "deviceId",
  "userName"
];

export const ANDON_DISPLAY_LINE_ORDER: AndonDisplayLine[] = [
  "icon",
  "status",
  "deviceId",
  "userName",
  "fcAbbreviation",
  "roles"
];

export const ANDON_DISPLAY_LINE_LABEL_KEY = {
  icon: "andon display line icon",
  status: "status",
  deviceId: "device id",
  userName: "user name",
  fcAbbreviation: "fc abbreviation",
  roles: "roles"
} as const satisfies Record<AndonDisplayLine, string>;

export type BoardTextLabelFields = {
  id: string;
  text: string;
  backgroundColor?: string;
  textColor?: string;
  fontSize?: number;
};

export const DEFAULT_TEXT_LABEL_FONT_SIZE = 14;
export const MIN_TEXT_LABEL_FONT_SIZE = 8;
export const MAX_TEXT_LABEL_FONT_SIZE = 72;

export const TEXT_ELEMENT_PREFIX = "label:";

export type BorderSide = "top" | "right" | "bottom" | "left";

export const BORDER_SIDES: BorderSide[] = ["top", "right", "bottom", "left"];

export const DEFAULT_CELL_BORDER_COLOR = "#000000";

export type AndonCellBorder = {
  x: number;
  y: number;
  sides: BorderSide[];
  color?: string;
};

export type AndonGridCell = {
  x: number;
  y: number;
};

export type BoardLayout = {
  name: string;
  dimensions: {
    rows: number;
    columns: number;
  };
  layout: AndonBoardLayoutItem[];
  scale: number;
  displayLines?: AndonDisplayLine[];
  /** @deprecated Use fulfillmentCenterIds instead */
  fulfillmentCenterId?: string;
  fulfillmentCenterIds?: string[];
  cellBorders?: AndonCellBorder[];
  /** @deprecated Migrated into kind:"text" layout items on load */
  textElements?: BoardTextLabelFields[];
};

export type BoardDimensions = {
  columns: number;
  rows: number;
};

export type WorkingLayout = {
  name: string | null;
  dimensions: BoardDimensions;
  layout: AndonBoardLayoutItem[];
  scale: number;
  displayLines: AndonDisplayLine[];
  cellBorders: AndonCellBorder[];
  fulfillmentCenterIds: string[];
};

export const EMPTY_WORKING_LAYOUT: WorkingLayout = {
  name: null,
  dimensions: { columns: 5, rows: 3 },
  layout: [],
  scale: 1,
  displayLines: DEFAULT_ANDON_DISPLAY_LINES,
  cellBorders: [],
  fulfillmentCenterIds: []
};

export type AddWorkstationsResult = {
  layout: AndonBoardLayoutItem[];
  dimensions: BoardDimensions;
};

export type WorkstationWithFulfillmentCenter = WorkstationSummaryDto & {
  fulfillmentCenterId?: string;
};

export type AndonWorkstationTooltipContent = {
  iconLabel?: string;
  statusText?: string;
  deviceId?: string;
  userName?: string;
  fcAbbreviation?: string;
  roles?: string;
};
